import React from 'react';
import { EIcMetadataXSource, EJedeaiMetadataXSource, Model } from '@sgdocs/client';
import { FiltersData, SGMDocsSearchFromModelLayout } from './components/SGMDocsSearchFromModelLayout';

const defaultLabels = {
  showFilters: 'Show model filters',
  hideFilters: 'Hide model filters',
  search: 'Search',
  inputPlaceholder: 'Search ...',
};

interface ISGMDocsSearchFromModelProps {
  searchValue?: string;
  onQuery: (query: string) => void;
  model: Model;
  initFilterFormData?: any;
  onFiltersReturned: (filters: any) => void;
  labels?: {
    inputPlaceholder?: string;
    hideFilters?: string;
    showFilters?: string;
    search?: string;
  };
}

const regexDateRange = /\d{4}-\d{2}-\d{2}#\d{4}-\d{2}-\d{2}/;

/**
 * Builds a date range query string for a given field.
 *
 * @param date - A string representing the date range, with start and end dates separated by a '#'.
 *               If only one date is provided, it will be used as either the start or end date.
 * @param field - The name of the field to query.
 * @param suffix - An optional suffix to append to the query string.
 * @returns A string representing the date range query for the specified field.
 *          Returns an empty string if the date is not provided or both start and end dates are missing.
 */
const buildDateRangeQuery = (date: string, field: string, suffix = ''): string => {
  if (!date) {
    return '';
  }

  const [startDate, endDate] = date.split('#');

  if (!startDate && !endDate) {
    return '';
  }

  if (!startDate && endDate) {
    return `${field} <= '${endDate}'${suffix}`;
  }

  if (startDate && !endDate) {
    return `${field} >= '${startDate}'${suffix}`;
  }

  return `${field} BETWEEN '${startDate}' AND '${endDate}'${suffix}`;
};

export const SGMDocsSearchFromModel = ({
  onFiltersReturned,
  onQuery,
  searchValue,
  model,
  labels,
  initFilterFormData,
}: ISGMDocsSearchFromModelProps): JSX.Element => {
  const labelsIntl = {
    ...defaultLabels,
    ...labels,
  };

  const onSubmit = (filtersData: FiltersData): void => {
    buildQuery(filtersData, model.jsonSchema);
    onFiltersReturned(filtersData.modelFilterData);
  };

  const buildQuery = (filtersData: FiltersData, jsonSchema: any): void => {
    let searchQuery: string = filtersData.extraSearch.inputSearch
      .split(' ')
      .map((word: string) => word.trim())
      .filter((word: string) => word)
      .map((s) => `SEARCH '${s}'`)
      .join(' AND ');

    if (searchQuery) {
      searchQuery += ' AND ';
    }

    if (filtersData.extraSearch.creationDate.length > 1) {
      searchQuery += buildDateRangeQuery(filtersData.extraSearch.creationDate, 'creationDate', ' AND ');
    }

    searchQuery += buildAdvancedSearch(filtersData.modelFilterData, jsonSchema);
    onQuery(searchQuery);
  };

  const buildAdvancedSearch = (filters: any, jsonSchema: any): string => {
    const metadataQuery: string[] = [];

    const addQuery = (query: string): void => {
      if (query) {
        metadataQuery.push(query);
      }
    };

    const processObjectArrayField = (key: string, field: string, values: any[], valueKey = ''): void => {
      if (values.length > 0) {
        if (valueKey) {
          const valuesList = values.map((value: any) => `'${value[valueKey]}'`).join(',');
          addQuery(`SOME i IN metadata.${key}.${field} SATISFIES i.${valueKey} IN (${valuesList})`);
        } else {
          const valuesList = values.map((value: any) => `'${value}'`).join(',');
          addQuery(`SOME i IN metadata.${key}.${field} SATISFIES i IN (${valuesList})`);
        }
      }
    };

    const processStringField = (key: string, value: any): void => {
      if (jsonSchema.properties[key].enum && jsonSchema.properties[key].enum.length > 0) {
        addQuery(`metadata.${key} = '${value}'`);
      } else if (jsonSchema.properties[key].format === 'date' && regexDateRange.test(value)) {
        addQuery(buildDateRangeQuery(value, `metadata.${key}`));
      } else if (jsonSchema.properties[key].format === 'date-time') {
        addQuery(`metadata.${key} = '${value}'`);
      } else {
        addQuery(`metadata.${key} LIKE '%${value}%'`);
      }
    };

    if (filters) {
      const processFilter = (key: string, value: any, property: any): void => {
        const xSource = property['x-source'];
        const type = property.type;

        if (type === 'string' && xSource === EIcMetadataXSource.IC_CLIENT && value.length > 0) {
          const valuesList = value.map((v: any) => `'${v}'`).join(',');
          addQuery(`metadata.${key} IN (${valuesList})`);
        } else if (type === 'object') {
          processObjectField(key, value, xSource);
        } else if (type === 'string' && value) {
          processStringField(key, value);
        } else if (['boolean', 'number'].includes(type)) {
          addQuery(`metadata.${key} = ${value}`);
        } else if (type === 'array' && value.length > 0) {
          const valuesList = value.map((v: any) => `'${v}'`).join(',');
          addQuery(`SOME i IN metadata.${key} SATISFIES i IN (${valuesList})`);
        }
      };

      const processObjectField = (key: string, value: any, xSource: any): void => {
        if ([EIcMetadataXSource.IC_LEGAL_ENTITY, EIcMetadataXSource.IC_ELEMENTARY_CLIENT].includes(xSource)) {
          addQuery(`metadata.${key}.icId IN (${value.map((value: any) => value.icId).join(',')})`);
        } else if (xSource === EJedeaiMetadataXSource.ELIOT_BASKET) {
          processObjectArrayField(key, 'basketId', value.basketId);
        } else if (xSource === EJedeaiMetadataXSource.ELIOT_FLOW) {
          processObjectArrayField(key, 'flowId', value.flowId);
        } else if (xSource === EJedeaiMetadataXSource.X_ONE_DEAL) {
          processObjectArrayField(key, 'dealId', value.dealId);
        } else if (
          [EJedeaiMetadataXSource.GALAXY_PRODUCT, EJedeaiMetadataXSource.SPARK_PRODUCT_ISSUE].includes(xSource)
        ) {
          processObjectArrayField(key, 'product', value.product, 'isinCode');
        } else if (xSource === EJedeaiMetadataXSource.SPARK_MONTAGE) {
          processObjectArrayField(key, 'montage', value.montage, 'fileCode');
        } else if (
          [
            EJedeaiMetadataXSource.MAESTRO_COUNTERPARTY,
            EJedeaiMetadataXSource.MAESTRO_CARE_COUNTERPARTY,
            EJedeaiMetadataXSource.MAESTRO_LEGAL_ENTITY,
          ].includes(xSource)
        ) {
          processObjectArrayField(key, 'bdr', value.bdr, 'bdrId');
        } else if (xSource === EJedeaiMetadataXSource.TERMSHEET_CONTROL && value.control) {
          addQuery(`metadata.${key}.control = '${value.control}'`);
        }
      };
      Object.keys(filters).forEach((key: string) => {
        const value = filters[key];
        if (value !== undefined && value !== null) {
          processFilter(key, value, jsonSchema.properties[key]);
        }
      });
    }

    const dviQuery = `(documentVersionIdentifier.workspaceId = '${model.workspaceId}' AND documentVersionIdentifier.modelId = '${model.id}')`;
    return `${dviQuery}` + (metadataQuery.length > 0 ? ` AND ${metadataQuery.join(' AND ')}` : '');
  };

  return (
    <SGMDocsSearchFromModelLayout
      model={model}
      searchValue={searchValue || ''}
      onSubmit={onSubmit}
      labels={labelsIntl}
      initFilterFormData={initFilterFormData}
    />
  );
};
